import { useState, useEffect, useMemo } from 'react';
import debounce from '@helpers/debounce';
import logger from '@logger';
import Config from '@config';
import { searchStores, getSuggestedPickupStore } from '@api/interfaces/storeApi';
import { trackSearchSuggestions } from '@helpers/analyticsHelpers/trackDeliveryWidget';
import { AxfoodStoreInfoViewModel } from '@occ/api-client';
import useCustomer from '@hooks/useCustomer';
import useStore from '@hooks/useStore';
import paths from '@constants/paths';
import useCart from '@hooks/useCart';
import { usePathname } from 'next/navigation';

export const usePickupLocationSearch = (minLength = 2) => {
  const { customer } = useCustomer();
  const { store } = useStore();
  const { cart } = useCart();

  const pathname = usePathname();
  const isCheckout = pathname === paths.CHECKOUT;

  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<AxfoodStoreInfoViewModel[]>([]);
  const [suggestionForYou, setSuggestionForYou] = useState<AxfoodStoreInfoViewModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSuggestions = async (query: string) => {
    try {
      const res = await searchStores(
        encodeURIComponent(query.toLowerCase()),
        'score-desc',
        undefined,
        undefined,
        customer?.isB2BCustomer,
        true
      );
      setSuggestions((res.results || []) as AxfoodStoreInfoViewModel[]);
      trackSearchSuggestions(
        false,
        (res.results || []) as AxfoodStoreInfoViewModel[],
        isCheckout,
        store?.storeId || ''
      );
    } catch (e) {
      if (e instanceof Error) {
        logger.error({ error: 'Failed to fetch pickup suggestions', message: e.message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useMemo(() => debounce(fetchSuggestions, Config.TIMEOUT.addressSearchGetSuggestionsMs), []);

  const onInputChange = (value: string) => {
    setInputValue(value);
    if (value.length >= minLength) {
      // Start loading before debounce to avoid flickering of empty results box
      setIsLoading(true);
      debouncedFetch(value);
    } else {
      setIsLoading(false);
      setSuggestions([]);
      debouncedFetch.cancel();
    }
  };

  useEffect(() => {
    const fetchInitialSuggestion = async () => {
      try {
        // Use current store if it's already valid
        let initialStore: AxfoodStoreInfoViewModel | undefined = store;

        const shouldFetchSuggestion =
          cart?.deliveryModeCode === Config.HOME_DELIVERY || !cart?.deliveryModeCode || !store?.activelySelected;

        if (shouldFetchSuggestion) {
          const { data: suggestedStore } = await getSuggestedPickupStore(false);
          initialStore = suggestedStore;
        }

        // Only show suggestion if it's a valid, active non external store
        if (initialStore?.activelySelected && !initialStore.external) {
          setSuggestionForYou({
            ...initialStore,
            displayName: initialStore.name,
          } as AxfoodStoreInfoViewModel);
        }
      } catch (e) {
        if (e instanceof Error) {
          logger.error({
            error: 'Failed to fetch suggested store',
            message: e.message,
          });
        }
      }
    };

    fetchInitialSuggestion();
  }, [store, cart?.deliveryModeCode]);

  return {
    inputValue,
    suggestions,
    suggestionForYou,
    isLoading,
    setInputValue,
    setSuggestions,
    onInputChange,
  };
};
